import { input } from '@b2b/types/core';
import angular, { ITimeoutService } from 'angular';
import './app-store.component.less';

export interface AppStoreTokenResponse {
  token: string;
}

export type AppStoreEnv =
  | 'legacy'
  | 'production'
  | 'sandbox'
  | 'development'
  | 'production_v2'
  | 'sandbox_v2';

export const DEFAULT_APP_STORE_ENV: AppStoreEnv = 'production_v2';

export type AppStoreElement = HTMLElement & {
  apiKey: string;
  env: AppStoreEnv;
  appId: string;
  theme: string;
  hideHelp: boolean;
  hideSuggest: boolean;
  getApiKey: () => Promise<string>;
};

export type AppStoreState = 'idle' | 'mounting' | 'mounted' | 'error';

class AppStoreController {
  static $inject = ['$element', '$timeout'];

  apiKey = input.required('');
  env = input.required<AppStoreEnv>(DEFAULT_APP_STORE_ENV);
  appId = input('');
  getApiKey: () => Promise<string>;

  state: AppStoreState = 'idle';

  // TODO: replace with `output` helper
  onMount: () => void;
  onUnmount: () => void;
  onError: (args: { $event: Error }) => void;

  private appStoreElement: AppStoreElement | null = null;
  private anchorElement: JQuery<HTMLElement> | null = null;

  constructor(private $element: JQuery, private $timeout: ITimeoutService) { }

  $onInit() {
    this.$element.addClass('app-store');
  }

  $postLink() {
    this.anchorElement = this.$element.find('[data-app-store-anchor]');
    this.mount();
  }

  $onDestroy() {
    this.unmount();
  }

  mount() {
    if (this.state === 'mounted') {
      this.unmount();
    }

    if (!this.apiKey || !this.anchorElement) {
      return;
    }

    this.state = 'mounting';

    try {
      this.appStoreElement = document.createElement('app-store') as AppStoreElement;
      this.appStoreElement.apiKey = this.apiKey;
      this.appStoreElement.env = this.env;
      this.appStoreElement.theme = 'acme';
      this.appStoreElement.hideHelp = true;
      this.appStoreElement.hideSuggest = true;
      this.appStoreElement.getApiKey = this.getApiKey;

      if (this.appId) {
        this.appStoreElement.appId = this.appId;
      }

      this.anchorElement?.append(this.appStoreElement);

      // CSS hack: Inject styles into shadow DOM to increase font size
      // Wait for shadow DOM to be ready, then inject custom styles
      this.$timeout(() => {
        this.injectShadowDOMStyles();
      }, 100);

      this.state = 'mounted';
      this.onMount?.();
    } catch (error) {
      this.state = 'error';
      this.onError?.({ $event: error as Error });
    }
  }

  private injectShadowDOMStyles() {
    if (!this.appStoreElement?.shadowRoot) {
      return;
    }

    // Create a style element to override font sizes in the shadow DOM
    const style = document.createElement('style');
    style.textContent = `
      * {
        font-size: 13px !important;
        line-height: 1.6 !important;
      }
    `;

    this.appStoreElement.shadowRoot.appendChild(style);
  }

  unmount() {
    if (this.appStoreElement) {
      this.appStoreElement.remove();
      this.appStoreElement = null;
      this.state = 'idle';
      this.onUnmount?.();
    }
  }
}

export const AppStoreComponentModule = angular
  .module('acme.view.application.app-store.components.appStore', [])
  .component('acmeAppStore', {
    templateUrl: 'views/application/views/app-store/components/app-store/app-store.component.tpl.html',
    controller: AppStoreController,
    bindings: {
      apiKey: '<',
      env: '<',
      appId: '<',
      getApiKey: '<',

      onMount: '&',
      onUnmount: '&',
      onError: '&',
    },
  }).name;
